import type { OutputData } from '@editorjs/editorjs';
import type { Block, TextStyle } from '@worldathletics/types';
import {
  defaultTextForStyle,
  styleForEditorTool,
} from '@worldathletics/utilities';
import {
  addNode,
  removeNode,
  reorderChildren,
  updateNode,
} from '@/context/store/slices/documentDataSlice';
import { chipHtmlToTokens } from './edit-text-serialize';

export type ParagraphRow = {
  id: string;
  text: string;
  style: TextStyle;
};

type Dispatcher = (action: { type: string; payload: unknown }) => void;

/** @deprecated Use `styleForEditorTool` from `@worldathletics/utilities`. */
export function styleForTool(toolName: string): TextStyle {
  return styleForEditorTool(toolName);
}

export function sameOrder(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Translate EditorJS-internal block ids (short auto-generated strings) to
 * stable DocumentData UUIDs. The mapping is mutated in place: callers should
 * hold it across saves so the same EditorJS block always resolves to the same
 * doc id. Without this, every save mints a fresh UUID for unknown ids and
 * `applyEditorJsOutput` churns remove/add dispatches on every keystroke after
 * a new block is created.
 */
export function translateBlockIds(
  output: OutputData,
  knownIds: Set<string>,
  mapping: Map<string, string>,
  liveEditorIds?: Set<string>,
): OutputData {
  const outputEditorIds = new Set<string>();
  const blocks = output.blocks.map(block => {
    const editorId = block.id ?? '';
    outputEditorIds.add(editorId);

    const mapped = mapping.get(editorId);
    if (mapped) return { ...block, id: mapped };
    if (knownIds.has(editorId)) return block;

    const docId = crypto.randomUUID();
    mapping.set(editorId, docId);
    return { ...block, id: docId };
  });

  // Drop mapping entries for editor ids no longer in the editor — keeps the
  // map from leaking entries for blocks the user has deleted. When the
  // caller supplies the editor's actual live block ids, prune against that
  // set instead: a block the editor still holds but that the saver skipped
  // (e.g. failed tool validation) must keep its UUID mapping.
  const liveIds = liveEditorIds ?? outputEditorIds;
  for (const editorId of mapping.keys()) {
    if (!liveIds.has(editorId)) mapping.delete(editorId);
  }

  return { ...output, blocks };
}

/**
 * Reinserts survivor ids (live blocks the saver skipped, so absent from
 * `outputOrder`) back into `outputOrder`, each placed right after its
 * nearest predecessor from `oldOrder` that is still present. Processes
 * survivors in their `oldOrder` sequence so multiple adjacent survivors keep
 * their relative order too.
 */
export function insertSurvivorsIntoOrder(
  oldOrder: string[],
  survivorIds: Set<string>,
  outputOrder: string[],
): string[] {
  const result = [...outputOrder];
  for (const survivorId of oldOrder.filter(id => survivorIds.has(id))) {
    const survivorOldIndex = oldOrder.indexOf(survivorId);
    let insertAt = 0;
    for (let i = survivorOldIndex - 1; i >= 0; i -= 1) {
      const idx = result.indexOf(oldOrder[i]);
      if (idx !== -1) {
        insertAt = idx + 1;
        break;
      }
    }
    result.splice(insertAt, 0, survivorId);
  }
  return result;
}

export function mergeStructuralOrder(
  parentChildren: string[],
  clusterPrev: string[],
  clusterNew: string[],
): string[] {
  if (clusterPrev.length === 0) return parentChildren;
  const firstIdx = parentChildren.indexOf(clusterPrev[0]);
  if (firstIdx === -1) return parentChildren;
  const before = parentChildren.slice(0, firstIdx);
  const after = parentChildren.slice(firstIdx + clusterPrev.length);
  return [...before, ...clusterNew, ...after];
}

type ApplyArgs = {
  parentId: string;
  prev: ParagraphRow[];
  output: OutputData;
  dispatch: Dispatcher;
  getParentChildren: () => string[];
  // Doc ids the EditorJS instance currently still holds (post id-translation).
  // When supplied, a `prev` id absent from `output.blocks` is only treated as
  // a deletion if it's also absent here — otherwise the saver merely skipped
  // an in-editor block (e.g. failed tool validation) and it must survive.
  liveEditorBlockIds?: Set<string>;
};

/**
 * Canonicalize inline HTML from EditorJS:
 *   <b> → <strong>, <i> → <em>
 *   Coalesce adjacent identical wraps
 *   Canonical nesting order outside-in: strong > em > span
 *
 * Normalizer only — does NOT sanitize. Combine with `sanitizeBlockHtml`.
 */
export function normalizeInlineHtml(input: string): string {
  let html = input
    .replace(/<b\b([^>]*)>/gi, '<strong$1>')
    .replace(/<\/b>/gi, '</strong>')
    .replace(/<i\b([^>]*)>/gi, '<em$1>')
    .replace(/<\/i>/gi, '</em>');
  html = html.replace(/<\/strong>\s*<strong>/g, '');
  html = html.replace(/<\/em>\s*<em>/g, '');
  return reorderNesting(html);
}

type InlineNode =
  | { kind: 'text'; text: string }
  | {
      kind: 'wrap';
      tag: 'strong' | 'em' | 'span';
      attrs: string;
      children: InlineNode[];
    };

const NESTING_PRIORITY: Record<'strong' | 'em' | 'span', number> = {
  strong: 0,
  em: 1,
  span: 2,
};

function reorderNesting(html: string): string {
  return parseInlineHtml(html).map(emitNode).join('');
}

function parseInlineHtml(html: string): InlineNode[] {
  type Token =
    | { type: 'text'; raw: string }
    | {
        type: 'open' | 'close';
        tag: 'strong' | 'em' | 'span';
        attrs: string;
      };
  const tokens: Token[] = [];
  let cursor = 0;
  for (const m of html.matchAll(/<(\/?)(strong|em|span)([^>]*)>/gi)) {
    const start = m.index ?? 0;
    if (start > cursor) tokens.push({ type: 'text', raw: html.substring(cursor, start) });
    const tag = m[2].toLowerCase() as 'strong' | 'em' | 'span';
    tokens.push({
      type: m[1] === '/' ? 'close' : 'open',
      tag,
      attrs: m[3] ?? '',
    });
    cursor = start + m[0].length;
  }
  if (cursor < html.length) tokens.push({ type: 'text', raw: html.substring(cursor) });

  const root: InlineNode[] = [];
  const stack: InlineNode[][] = [root];
  const tagStack: { tag: 'strong' | 'em' | 'span' }[] = [];

  for (const tok of tokens) {
    const current = stack[stack.length - 1];
    if (tok.type === 'text') {
      current.push({ kind: 'text', text: tok.raw });
    } else if (tok.type === 'open') {
      const node: InlineNode = {
        kind: 'wrap',
        tag: tok.tag,
        attrs: tok.attrs,
        children: [],
      };
      current.push(node);
      stack.push(node.children);
      tagStack.push({ tag: tok.tag });
    } else {
      if (tagStack.length > 0 && tagStack[tagStack.length - 1].tag === tok.tag) {
        tagStack.pop();
        stack.pop();
      }
    }
  }
  return reorderTree(root);
}

function reorderTree(nodes: InlineNode[]): InlineNode[] {
  return nodes.map(n => {
    if (n.kind === 'text') return n;
    return swapDownIfNeeded({ ...n, children: reorderTree(n.children) });
  });
}

function swapDownIfNeeded(node: InlineNode): InlineNode {
  if (node.kind === 'text') return node;
  if (node.children.length === 1 && node.children[0].kind === 'wrap') {
    const child = node.children[0];
    if (NESTING_PRIORITY[child.tag] < NESTING_PRIORITY[node.tag]) {
      return swapDownIfNeeded({
        kind: 'wrap',
        tag: child.tag,
        attrs: child.attrs,
        children: [
          swapDownIfNeeded({
            kind: 'wrap',
            tag: node.tag,
            attrs: node.attrs,
            children: child.children,
          }),
        ],
      });
    }
  }
  return node;
}

function emitNode(node: InlineNode): string {
  if (node.kind === 'text') return node.text;
  const inner = node.children.map(emitNode).join('');
  if (node.tag === 'span') {
    const attrs = node.attrs.trim();
    return `<span${attrs ? ` ${attrs}` : ''}>${inner}</span>`;
  }
  return `<${node.tag}>${inner}</${node.tag}>`;
}

export function canonicalizeStoredText(text: string): string {
  return normalizeInlineHtml(chipHtmlToTokens(text));
}

function resolvePersistedText(style: TextStyle, text: string): string {
  if (text.trim().length > 0) return text;
  return defaultTextForStyle(style);
}

/**
 * Applies EditorJS's saved output as add/update/remove/reorder dispatches.
 * Returns the resulting effective cluster order (doc ids), including any
 * survivor ids the saver skipped but that are still live in the editor —
 * callers should use this, not `output.blocks`, to rebuild their own view of
 * this cluster's siblings.
 */
export function applyEditorJsOutput(args: ApplyArgs): string[] {
  const {
    parentId,
    prev,
    output,
    dispatch,
    getParentChildren,
    liveEditorBlockIds,
  } = args;
  const prevById = new Map(prev.map(p => [p.id, p]));
  const nextIds = new Set(output.blocks.map(b => b.id ?? ''));

  // Snapshot the parent's children BEFORE any dispatch. `mergeStructuralOrder`
  // below splices `clusterNew` over the cluster's OLD span, which it locates as
  // `[indexOf(clusterPrev[0]), + clusterPrev.length)`. That span is only valid
  // against a pre-mutation list: `removeNode` (and `addNode`) already rewrite
  // `childrenById[parentId]`, so reading it afterwards leaves the window the
  // old length over a now-shorter list — over-consuming exactly as many
  // following siblings as blocks were removed, and silently orphaning them
  // (e.g. merging two paragraphs with Backspace dropped the neighbouring Rule
  // container, subtree and all, and shipped that truncation as a reorder op).
  const parentChildrenBefore = getParentChildren();

  for (const id of prevById.keys()) {
    if (!nextIds.has(id) && !liveEditorBlockIds?.has(id)) {
      dispatch(removeNode({ id }));
    }
  }

  output.blocks.forEach((block, index) => {
    const id = block.id ?? '';
    const blockData = block.data as { text?: string };
    const rawText = normalizeInlineHtml(chipHtmlToTokens(blockData.text ?? ''));
    const style = styleForEditorTool(block.type);
    const text = resolvePersistedText(style, rawText);
    const prevRow = prevById.get(id);
    if (!prevRow) {
      const node: Block = {
        id,
        type: 'text',
        style,
        order: index,
        text,
      };
      dispatch(
        addNode({
          node,
          parentId,
          index,
        }),
      );
    } else {
      const textChanged = canonicalizeStoredText(prevRow.text) !== text;
      const styleChanged = prevRow.style !== style;
      if (textChanged || styleChanged) {
        dispatch(
          updateNode({
            id,
            changes: {
              ...(textChanged ? { text } : {}),
              ...(styleChanged ? { style } : {}),
            },
          }),
        );
      }
    }
  });

  const outputClusterOrder = output.blocks.map(b => b.id ?? '');
  const oldClusterOrder = prev.map(p => p.id);

  // Survivors (live blocks the saver skipped) are absent from `output.blocks`
  // and therefore from `outputClusterOrder`. Reinsert each one next to its
  // nearest still-present predecessor from `oldClusterOrder` so it keeps its
  // place in the sibling order instead of being silently dropped to the end
  // (or out of `childrenById` entirely) by the reorder dispatch below.
  const survivorIds = new Set(
    [...prevById.keys()].filter(
      id => !nextIds.has(id) && liveEditorBlockIds?.has(id),
    ),
  );
  const newClusterOrder =
    survivorIds.size === 0
      ? outputClusterOrder
      : insertSurvivorsIntoOrder(oldClusterOrder, survivorIds, outputClusterOrder);

  if (!sameOrder(newClusterOrder, oldClusterOrder)) {
    dispatch(
      reorderChildren({
        parentId,
        childIds: mergeStructuralOrder(
          parentChildrenBefore,
          oldClusterOrder,
          newClusterOrder,
        ),
      }),
    );
  }

  return newClusterOrder;
}
